import { Currency, PaymentMethod, PaymentStatus } from '@/domain/payments/payments'
import { useCreateAgreementPayment } from '@/hooks/api/useAgreements'
import { useDeleteDirectPayment, useResetInstallmentPayment } from '@/hooks/api/usePayments'
import { useCreateBillingPeriodPayment, useDeleteBillingPeriod } from '@/hooks/api/useSubscriptions'
import { Paths } from '@/routes/paths'
import { useNavigate } from '@tanstack/react-router'

export const useAgreementPaymentActions = ({ userId }: { userId: string }) => {
  const navigate = useNavigate()
  const createAgreementPayment = useCreateAgreementPayment()
  const createBillingPeriodPayment = useCreateBillingPeriodPayment()
  const deleteBillingPeriod = useDeleteBillingPeriod()
  const deletePayment = useDeleteDirectPayment()
  const resetInstallmentPayment = useResetInstallmentPayment()

  const onPaymentRequest = ({ contractId, amountOwed, paymentId }: { contractId: string, amountOwed?: number, paymentId?: string }) => {
    if (!paymentId && amountOwed) {
      createAgreementPayment.mutate({
        agreementId: contractId,
        payment: {
          userId: userId,
          status: PaymentStatus.PENDING,
          currency: Currency.ARS,
          paymentMethod: PaymentMethod.OTHER,
        }
      })
    }
    else {
      navigate({ to: `${Paths.Management.Contracts}/${contractId}/${Paths.Management.Payments}/${paymentId}` })
    }
  }

  const onSubscriptionPaymentRequest = ({ billingPeriodId, paymentId }: { billingPeriodId: string, paymentId: string }) => {
    if (!billingPeriodId || !paymentId) return;
    navigate({ to: `${Paths.Management.SubscriptionPeriodPayments}/${billingPeriodId}/${Paths.Management.Payments}/${paymentId}` })
  }

  const onMultiSubscriptionPaymentRequest = ({ subscriptionId, paymentId }: { subscriptionId: string, paymentId: string }) => {
    if (!subscriptionId || !paymentId) return;
    navigate({ to: `${Paths.Management.SubscriptionPayments}/${subscriptionId}/${Paths.Management.Payments}/${paymentId}` })
  }

  const onNewSubscriptionPaymentRequest = (contractId: string, billingPeriodId: string) => {
    if (!billingPeriodId || !contractId) return;
    createBillingPeriodPayment.mutate({ contractId, billingPeriodId })
  }

  const onDeleteDirectPayment = (paymentId: string, contractId: string) => {
    deletePayment.mutate({ paymentId, contractId });
  }

  const onDeletePeriodPayment = (subscriptionId: string, billingPeriodId: string) => {
    deleteBillingPeriod.mutate({ subscriptionId, billingPeriodId });
  }

  const onResetInstallmentPayment = async ({ paymentId, contractId }: { paymentId: string; contractId: string }) => {
    await resetInstallmentPayment.mutateAsync({ paymentId, contractId })
    navigate({ to: `${Paths.Management.Contracts}/${contractId}/${Paths.Management.Payments}/${paymentId}` });
  }

  return {
    onPaymentRequest,
    onSubscriptionPaymentRequest,
    onMultiSubscriptionPaymentRequest,
    onNewSubscriptionPaymentRequest,
    onDeleteDirectPayment,
    onDeletePeriodPayment,
    onResetInstallmentPayment
  }
}
